import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import BasicModal from '../Modal';
import { NormalButton } from 'components/atoms/Button';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TagIcon from '@mui/icons-material/Tag';
import Switch from '@mui/material/Switch';
import { Alert, Box, createTheme, ThemeProvider } from '@mui/material';
import CopyToClickButton from 'components/atoms/Button/CopyToClipButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { updateUser } from 'APIs/AuthApi';
import { useAppSelector } from 'app/hooks';
import { selectAuth } from 'features/auth/authSlice';
import { getBackstageToken } from 'APIs/UserDataApi';
import Loader from 'components/atoms/loader';
interface ICampaignModalProps {
  children: JSX.Element;
}

const InputStyles = {
  backgroundColor: '#312F40',
  position: 'relative',
  width: '100%',
  maxWidth: '350px',
  borderRadius: '25px',
  padding: '20px 0px 20px 50px',
  border: 'none',
  fontSize: '16px',
  color: 'white',
} as React.CSSProperties;

const theme = createTheme({
  components: {
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          // Controls default (unchecked) color for the thumb
          color: '#fff',
        },
        colorPrimary: {
          '&.Mui-checked': {
            // Controls checked color for the thumb
            color: '#fff',
          },
        },
        track: {
          // Controls default (unchecked) color for the track
          backgroundColor: '#DDB930',
          opacity: 0.5,
          '.Mui-checked.Mui-checked + &': {
            // Controls checked color for the track
            backgroundColor: '#DDB930',
            opacity: 1,
          },
        },
      },
    },
  },
});

const CampaignModal: React.FC<ICampaignModalProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const authState = useAppSelector(selectAuth);
  const [date, setDate] = useState<Date | null>(null);
  const [dateOpen, setDateOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [unique, setUnique] = useState<boolean>(true);
  const [discountedPrice, setDiscountedPrice] = useState<string>();
  const [sales, setSales] = useState<number>(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [token, setToken] = useState('');
  const [isTrial, setIsTrial] = useState(true);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSuccess(false);
    setUnique(true);
  };
  const label = { inputProps: { 'aria-label': 'Switch demo' } };

  useEffect(() => {
    const getToken = async () => {
      if (authState.username) {
        const res = await getBackstageToken(authState.username);
        setToken(res.data);
      }
    };
    getToken();
  }, [authState.username]);

  const submitCampaign = async () => {
    setLoading(true);
    try {
      if (discountedPrice && date) {
        await updateUser({
          id: authState.id,
          trialDuration: date,
          isOnTrial: false,
          discountedSubscriptionPrice: discountedPrice,
          numberOfSales: 0,
          uniqueUrl: unique,
        });
      } else if (discountedPrice && sales) {
        await updateUser({
          id: authState.id,
          trialDuration: null,
          isOnTrial: false,
          discountedSubscriptionPrice: discountedPrice,
          numberOfSales: sales,
          uniqueUrl: unique,
        });
      } else if (date && sales) {
        await updateUser({
          id: authState.id,
          trialDuration: date,
          isOnTrial: false,
          discountedSubscriptionPrice: null,
          numberOfSales: sales,
          uniqueUrl: unique,
        });
      } else if (date) {
        await updateUser({
          id: authState.id,
          trialDuration: date,
          isOnTrial: true,
          discountedSubscriptionPrice: null,
          numberOfSales: 0,
          uniqueUrl: unique,
        });
      }
      setError(false);
      setSuccess(true);
    } catch (error) {
      console.log(error);
      setError(true);
      setSuccess(false);
    }
    setLoading(false);
  };

  return (
    <div>
      <span onClick={handleOpen}>{children}</span>
      <BasicModal open={open} handleClose={handleClose}>
        {!success ? (
          <>
            <Typography
              sx={{ textAlign: 'center', color: 'white' }}
              id="modal-modal-title"
              variant="h6"
              component="h2"
            >
              Create Campaign/Trial
            </Typography>
            {error && (
              <Alert
                sx={{ marginTop: '20px' }}
                severity="error"
                onClose={() => setError(false)}
              >
                There was an error setting up your campaign/trial.
              </Alert>
            )}

            <Typography
              id="modal-modal-description"
              sx={{ my: 4, textAlign: 'center', opacity: '0.6' }}
            >
              Setup a discounted amount on your subscription price. If left
              empty, the number will be infinite and time-based.
            </Typography>

            <Box>
              <NormalButton
                sx={{ width: '48%', marginRight: '4%' }}
                text="Trial"
                onClick={() => setIsTrial(true)}
                color={isTrial ? 'yellow' : undefined}
              />
              <NormalButton
                onClick={() => setIsTrial(false)}
                sx={{ width: '48%' }}
                text="Campaign"
                color={!isTrial ? 'yellow' : undefined}
              />
            </Box>
            {!isTrial && (
              <div style={{ position: 'relative', margin: '15px 0px' }}>
                <AttachMoneyIcon
                  sx={{
                    opacity: '0.6',
                    color: 'white',
                    position: 'absolute',
                    left: 15,
                    top: 15,
                    zIndex: 10,
                  }}
                />
                <input
                  style={InputStyles}
                  placeholder="Price during campaign"
                  type="text"
                  onChange={e => setDiscountedPrice(e.target.value)}
                />
              </div>
            )}
            <div
              style={{
                position: 'relative',
                margin: '15px 0px',
              }}
            >
              <AccessTimeIcon
                sx={{
                  opacity: '0.6',
                  color: 'white',
                  position: 'absolute',
                  left: 15,
                  top: 17,
                  zIndex: 10,
                }}
              />
              <input
                style={InputStyles}
                placeholder="Duration"
                onChange={e => setDate(e.target.valueAsDate)}
                type={dateOpen ? 'date' : 'text'}
                onFocus={() => setDateOpen(true)}
                onBlur={() => setDateOpen(false)}
              />
              <KeyboardArrowDownIcon
                sx={{
                  opacity: '0.6',
                  color: 'white',
                  position: 'absolute',
                  right: 15,
                  top: 17,
                  zIndex: 10,
                }}
              />
            </div>
            <div style={{ position: 'relative', margin: '15px 0px' }}>
              <TagIcon
                sx={{
                  opacity: '0.6',
                  color: 'white',
                  position: 'absolute',
                  left: 15,
                  top: 15,
                  zIndex: 10,
                }}
              />
              <input
                style={InputStyles}
                placeholder={`Amount of ${
                  isTrial ? 'claims' : 'sales'
                } remaining`}
                type="text"
                onChange={e => setSales(parseInt(e.target.value))}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '30px 0px',
              }}
            >
              <span style={{ color: 'white' }}>Unique URL Only</span>
              <ThemeProvider theme={theme}>
                <Switch
                  onChange={() => setUnique(!unique)}
                  value={unique}
                  {...label}
                  defaultChecked
                />
              </ThemeProvider>
            </div>
            <NormalButton
              text={loading ? <Loader size="2" /> : 'Generate'}
              onClick={submitCampaign}
              color="yellow"
              sx={{ width: '100%', textTransform: 'uppercase' }}
              isDisabled={loading}
            />
          </>
        ) : (
          <>
            <Typography
              sx={{ textAlign: 'center', color: 'white' }}
              id="modal-modal-title"
              variant="h6"
              component="h2"
            >
              Congratulations!
            </Typography>

            <Typography
              id="modal-modal-description"
              sx={{ my: 4, textAlign: 'center', opacity: '0.6' }}
            >
              Your campaign was successfully created.
              {unique && 'Here is your URL assosciated to your campaign:'}
            </Typography>
            {unique && (
              <div style={{ position: 'relative', margin: '30px 0px' }}>
                <CopyToClickButton
                  style={{ width: '100%' }}
                  text={`${window.location.href.split('/')[2]}/profile/${
                    authState.username
                  }/Backstage?token=${token}`}
                />
              </div>
            )}
            <NormalButton
              onClick={handleClose}
              text={'Continue'}
              color="yellow"
              sx={{ width: '100%', textTransform: 'uppercase' }}
            />
          </>
        )}
      </BasicModal>
    </div>
  );
};

export default CampaignModal;
